import qs from 'qs';
import { appointmentDraftSelectors, appointmentDraftActions, shopsApi } from '~/api';
import { formatSlotsData } from '~/components/calendar-with-filters/helpers';
import { errorMessages, errorMessagesActions, errorTypes } from '~/components/messages';
import { dateUtils } from '../utils';
import { isEmpty, hasValuePredicate } from '~/utils';

const { NO_AVAILABLE_PARTNERS_ERROR, SOMETHING_WENT_WRONG_ERROR } = errorTypes;
const actionPrefix = (actionType) => `SelectDate/${actionType}`;

export const SET_SLOTS = actionPrefix`SET_SLOTS`;
export const setSlots = (slots) => ({
  type: SET_SLOTS,
  payload: slots,
});

export const SET_NEXT_SLOTS = actionPrefix`SET_NEXT_SLOTS`;
export const setNextSlots = (slots) => ({
  type: SET_NEXT_SLOTS,
  payload: slots,
});

export const SET_PREVIOUS_SLOTS = actionPrefix`SET_PREVIOUS_SLOTS`;
export const setPreviousSlots = (slots) => ({
  type: SET_PREVIOUS_SLOTS,
  payload: slots,
});

export const SET_FILTERS = actionPrefix`SET_FILTERS`;
export const setFilters = (filter) => ({
  type: SET_FILTERS,
  payload: filter,
});

export const REPLACE_FILTERS = actionPrefix`REPLACE_FILTERS`;
export const replaceFilters = (filter) => ({
  type: REPLACE_FILTERS,
  payload: filter,
});

export const createQueryParams = (
  {
    addons,
    appointmentType: {
      id: appointmentTypeId,
      duration,
      minAdvance,
      localId: localAppointmentTypeId,
    },
    partner: { id: partnerId },
    partnersGroupId,
  },
  rangeStartDate,
  rangeEndDate,
  token,
) =>
  qs.stringify(
    {
      appointmentTypeId: [appointmentTypeId, ...addons.map((addon) => addon.appointmentType.id)]
        .filter(hasValuePredicate)
        .join(','),
      partnerId,
      duration: [duration, ...addons.map((addon) => addon.appointmentType.duration)]
        .filter(hasValuePredicate)
        .join(','),
      minAdvance: [minAdvance, ...addons.map((addon) => addon.appointmentType.minAdvance)]
        .filter(hasValuePredicate)
        .join(','),
      localAppointmentTypeId: [
        localAppointmentTypeId,
        ...addons.map((addon) => addon.appointmentType.localId),
      ]
        .filter(hasValuePredicate)
        .join(','),
      startDate: rangeStartDate,
      endDate: rangeEndDate,
      token: token !== 'null' ? token : undefined,
      partnersGroupId: isEmpty(partnersGroupId) ? undefined : partnersGroupId,
    },
    { skipNulls: true, encode: false },
  );

const formatSlots = (resolvedSlots, startDate, eligibleSlotsFilter) =>
  resolvedSlots.map((monthSlot, index) => {
    const monthStartDate = index === 0 ? startDate : undefined;
    return formatSlotsData(monthSlot.slots, monthStartDate, eligibleSlotsFilter);
  });

const hasAvailableSlots = (formattedSlots) =>
  formattedSlots.some((slot) => slot?.dates.some((date) => date.times.length));

const loadSlots = async (
  dispatch,
  getState,
  startDate,
  slotsAction,
  eligibleSlotsFilter,
  monthCount = 1,
  token = undefined,
) => {
  const state = getState();
  const { id: shopId, name: shopName } = appointmentDraftSelectors.selectShop(state);
  const appointmentDraft = appointmentDraftSelectors.selectAppointmentDraft(state);
  const monthsRanges = Object.values(dateUtils.getMonthsRangesForDuration(startDate, monthCount));
  const [firstSlotURL, secondSlotURL] = monthsRanges.map(
    ({ startDate: rangeStartDate, endDate: rangeEndDate }) =>
      `/${shopId}/slots?${createQueryParams(appointmentDraft, rangeStartDate, rangeEndDate, token)}`,
  );

  try {
    const bypassErrorCodes = shopId ? [404] : [];
    const firstSlot = await shopsApi.fetch(firstSlotURL, {}, bypassErrorCodes);
    const firstSlotFormatted = formatSlots([firstSlot], startDate, eligibleSlotsFilter);

    if (secondSlotURL && secondSlotURL.length && !hasAvailableSlots(firstSlotFormatted)) {
      const secondSlot = await shopsApi.fetch(secondSlotURL, {}, bypassErrorCodes);

      dispatch(
        slotsAction([
          ...firstSlotFormatted,
          ...formatSlots([secondSlot], undefined, eligibleSlotsFilter),
        ]),
      );
    } else {
      dispatch(slotsAction(firstSlotFormatted));
      if (secondSlotURL && secondSlotURL.length) {
        const secondSlot = await shopsApi.fetch(secondSlotURL, {}, bypassErrorCodes);
        const secondSlotFormatted = formatSlots([secondSlot], undefined, eligibleSlotsFilter);

        dispatch(setNextSlots(secondSlotFormatted));
      }
    }
  } catch ({ status }) {
    if (status === 404) {
      const actionMessage = {
        type: NO_AVAILABLE_PARTNERS_ERROR,
        title: errorMessages[NO_AVAILABLE_PARTNERS_ERROR].createTitle(shopName),
      };

      dispatch(errorMessagesActions.resetErrorMessagesWith([actionMessage]));
    } else {
      const actionMessage = {
        type: SOMETHING_WENT_WRONG_ERROR,
        title: errorMessages[SOMETHING_WENT_WRONG_ERROR].title,
      };

      dispatch(errorMessagesActions.resetErrorMessagesWith([actionMessage]));
    }
  }
};

export const loadInitialSlots =
  (startDate, monthCount, eligibleSlotsFilter, token = undefined) =>
  (dispatch, getState) =>
    loadSlots(dispatch, getState, startDate, setSlots, eligibleSlotsFilter, monthCount, token);

export const loadPreviousSlots = (startDate, eligibleSlotsFilter) => (dispatch, getState) =>
  loadSlots(dispatch, getState, startDate, setPreviousSlots, eligibleSlotsFilter);

export const loadNextSlots = (startDate, eligibleSlotsFilter) => (dispatch, getState) =>
  loadSlots(dispatch, getState, startDate, setNextSlots, eligibleSlotsFilter);

export const loadFittedBedroomsSlots =
  (postcode, startDate, eligibleSlotsFilter) => (dispatch, getState) => {
    const {
      appointment: {
        draft: {
          appointmentType: {
            id: appointmentTypeId,
            duration,
            minAdvance,
            maxAdvance,
            localId: localAppointmentTypeId,
          },
          shop: { id: shopId },
          dateSlot,
        },
      },
    } = getState();

    const query = qs.stringify(
      {
        appointmentTypeId,
        duration,
        minAdvance,
        localAppointmentTypeId,
        startDate,
        endDate: dateUtils.getFormattedDate(new Date(new Date().getTime() + maxAdvance)),
        postcode,
      },
      { skipNulls: true },
    );

    return shopsApi
      .fetch(`/${shopId}/slots?${query}`, {}, shopId ? [404] : [])
      .then(({ slots }) => {
        const isSlotAvailable = slots.some(
          ({ date, times }) => date === dateSlot && times.length > 0,
        );
        if (!isSlotAvailable) {
          dispatch(appointmentDraftActions.setDateSlot(''));
        }

        const groupedSlots = slots.reduce(
          (reducer, element) => {
            let newReducer = reducer;
            const month = element.date.split('-')[1];
            if (newReducer[month]) {
              const startDateSlots = newReducer[month].find((el) => el.date === element.date);
              if (startDateSlots) {
                startDateSlots.times = element.times;
              } else {
                newReducer[month].push(element);
              }
            } else {
              newReducer = {
                ...newReducer,
                [month]: [element],
              };
            }
            return newReducer;
          },
          { [startDate.split('-')[1]]: [{ date: startDate, times: [] }] },
        );

        const formattedSLots = Object.keys(groupedSlots).map((month) =>
          formatSlotsData(groupedSlots[month], groupedSlots[month][0].date, eligibleSlotsFilter),
        );
        dispatch(setSlots(formattedSLots));
      });
  };

export const selectDateActions = {
  setFilters,
};
